import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import {
  Alert,
  Badge,
  FlexContainer,
  LoadingIndicator,
  ModalButtonWithForm,
  Tooltip,
  User
} from 'components/common';
import { FormGroup, Select } from 'components/common/form';

import type { UserWithBasicData } from 'shared-types';
import type { GetListOfPossibleConfidentialUsersRes } from 'shared-types/responses';

type FormValues = { users: string[] };

interface Props {
  achievementCompletionId: number;
  achievementId: number;
  disabled: boolean;
  isConfidential: boolean;
  isUserOrMember: boolean;
  targetUserId?: number;
  updateAchievement: () => Promise<void>;
  users: UserWithBasicData[];
}

const AchievementCompletionsSetConfidential: React.FC<Props> = ({
  achievementCompletionId,
  achievementId,
  disabled = true,
  isConfidential,
  isUserOrMember,
  targetUserId,
  updateAchievement,
  users
}) => {
  const { dispatch } = useDispatch();

  const { messages, user: requestUser } = useReduxState();

  const { data, loading, updateData } =
    useApiData<GetListOfPossibleConfidentialUsersRes>({
      url:
        `/admin/achievements/${achievementId}` +
        `/completions/confidential/${achievementCompletionId}` +
        (isUserOrMember
          ? `?userId=${requestUser?.id}`
          : targetUserId
          ? `?userId=${targetUserId}`
          : ''),
      onlyIf: Boolean(achievementCompletionId)
    });

  const findUser = (identifier: string) => {
    return data?.confidentialUsers.find((user) => {
      const id = user.email || user.username;
      return id === identifier;
    });
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const confidentialUserIds = values.users.map(
        (identifier) => findUser(identifier)?.id
      );

      await api.put(
        `/admin/achievements/${achievementId}/completions/set-confidential`,
        {
          confidentialUserIds,
          completionId: achievementCompletionId,
          ...(isUserOrMember ? { userId: requestUser.id } : {})
        }
      );

      dispatch.addNotice({
        text: messages.achievementCompletionConfidentialSuccess,
        type: 'success'
      });

      await updateAchievement();
      await updateData();
    } catch (err) {
      dispatch.handleErrorDispatch(err);
    }
  };

  const canRequestUserSetAsConfidential =
    isUserOrMember ||
    data?.confidentialUsers.some((user) => user.id === requestUser?.id);

  return isConfidential ? (
    <Tooltip
      content={
        <FormGroup
          label={messages.achievementCompletionConfidentialUsersWithAccess}
          style={{ padding: 10 }}
        >
          <FlexContainer
            style={{
              alignItems: 'flex-start',
              flexDirection: 'column',
              gap: 10,
              paddingTop: 5
            }}
          >
            {data?.confidentialUsers?.map((user) => (
              <User key={user.id} user={user} />
            ))}
          </FlexContainer>
        </FormGroup>
      }
      isLight={true}
    >
      <Badge
        category="error-light"
        icon="lock"
        text={messages.commonConfidential}
      />
    </Tooltip>
  ) : disabled || !canRequestUserSetAsConfidential ? null : (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonIcon="lock"
      buttonSize="header"
      buttonText={messages.achievementCompletionConfidential}
      initialValues={{ userIds: [] }}
      modalActionCategory="danger"
      modalActionText={messages.achievementCompletionConfidential}
      modalTitle={messages.achievementCompletionConfidential}
      modalWidth="medium"
      onSubmit={handleSubmit}
    >
      {({ values }: { values: FormValues }) =>
        loading ? (
          <LoadingIndicator />
        ) : (
          <>
            <Alert
              style={{ marginBottom: 20 }}
              text={messages.achievementCompletionConfidentialWarning}
              type="warning-light"
            />

            <FormGroup label={messages.commonAddUsers} maxWidth="100%">
              <Select
                disabled={!data?.confidentialUsers?.length}
                field="users"
                id="users"
                isMulti={true}
                isRequired={true}
                menuWidth="360px"
                options={data?.confidentialUsers.map((user) => ({
                  label: <User user={user} />,
                  value: user.email || user.username
                }))}
              />
            </FormGroup>

            <FormGroup
              label={messages.achievementCompletionConfidentialUsersWithAccess}
            >
              <FlexContainer
                style={{ flexWrap: 'wrap', gap: '10px 20px', padding: '5px 0' }}
              >
                {users.map((user) => (
                  <User key={user.id} user={user} />
                ))}

                {values.users?.map((userId) => (
                  <User key={userId} user={findUser(userId)} />
                ))}
              </FlexContainer>
            </FormGroup>
          </>
        )
      }
    </ModalButtonWithForm>
  );
};

export default AchievementCompletionsSetConfidential;
